// Intel cards (GDD §14): what the HUD's Intel panel shows for the thing under the pointer or keyboard focus — an
// Arsenal unit, a strike, a building option or a city on the globe. Pure: content and the given state in, a card out.
// Each effect, target class and domain has one describing entry, so a new content kind is a compile error here.

import {
  BUILDINGS,
  CAPTURE,
  COUNTRIES,
  STRIKES,
  UNITS,
  type BuildingEffect,
  type BuildingKey,
  type Domain,
  type MissileGuidance,
  type StrikeEffect,
  type TargetClass,
} from '@taxwar/content'
import { NEUTRAL_OWNER, type MapCityView, type RejectReason } from '@taxwar/net'
import type { CityLiveState } from '../match/contracts'
import type { SiteState } from './bars'
import { BUILDING_PRESENTATION, UNIT_PRESENTATION } from './catalog'
import { HudError } from './errors'
import { formatMoney, formatMoneyRate, formatWhole } from './format'
import type { StrikeItem, UnitItem } from './items'
import { REJECT_COPY } from './rejects'
import { buildingKeyOf } from './rules'
import type { WarMapIndex } from './warMap'

/** How a value reads: plain text, a good or bad state, money, or an owner's faction colour. */
export type InfoTone = 'plain' | 'good' | 'bad' | 'money' | 'player' | 'enemy' | 'neutral'

export interface InfoRow {
  readonly label: string
  readonly value: string
  readonly tone: InfoTone
}

export interface InfoCard {
  /** Category line above the title, e.g. "UNIT · SEA". */
  readonly kicker: string
  readonly title: string
  readonly rows: readonly InfoRow[]
  /** How to use it, or a deadpan line; null for none. */
  readonly note: string | null
  /** Sponsor credit; null for none. */
  readonly footer: string | null
}

type EffectOf<U extends { readonly kind: string }, K extends U['kind']> = Extract<U, { readonly kind: K }>

const MET_MARK = '✓'
const UNMET_MARK = '✗'

function row(label: string, value: string, tone: InfoTone = 'plain'): InfoRow {
  return { label, value, tone }
}

function sponsored(sponsor: string): string {
  return `Sponsored by ${sponsor}`
}

function seconds(value: number): string {
  return `${value} s`
}

// ------------------------------------------------------------------------------------------------------- units

const DOMAIN_LABELS: Readonly<Record<Domain, string>> = { land: 'LAND', sea: 'SEA', air: 'AIR' }

const TARGET_LABELS: Readonly<Record<TargetClass, string>> = {
  any: 'Aircraft, ground units and structures',
  ground: 'Ground units and structures',
  structures_only: 'Structures only',
  air_only: 'Aircraft only',
  air_and_ground_units: 'Aircraft and ground units',
  none: 'Nothing: it launches fighters',
}

export function unitCard(item: UnitItem, site: SiteState): InfoCard {
  const def = UNITS[item.key]
  const rows: InfoRow[] = [row('Cost', formatMoney(def.cost), 'money'), row('Deploys', item.doctrine.rule)]
  const requirement = item.requirement
  if (requirement !== null) {
    const mark = site === 'met' ? MET_MARK : UNMET_MARK
    rows.push(requirement.needsBuilding ? row('Requires', `${mark} ${requirement.rule}`, site === 'met' ? 'good' : 'bad') : row('Requires', requirement.rule))
  }
  rows.push(row('Strength', def.bodies > 1 ? `${def.bodies} × ${formatWhole(def.hpPerBody)} HP` : `${formatWhole(def.hpPerBody)} HP`))
  if (def.targetClass !== 'none') {
    const splash = def.splashRadius > 0 ? `, splash ${def.splashRadius}` : ''
    const reach = def.minRange > 0 ? `${def.minRange}–${def.range}` : `${def.range}`
    const damage = def.bodies > 1 ? `${def.damage} dmg each` : `${def.damage} dmg`
    rows.push(row('Weapon', `${damage} every ${seconds(def.attackPeriod)}, range ${reach}${splash}`))
  }
  rows.push(row('Targets', TARGET_LABELS[def.targetClass]), row('Speed', `${def.speed} map units/s`))
  if (def.captures) rows.push(row('Takes cities', 'Yes: fallen enemy cities and free neutral ones'))
  const spawner = def.spawner
  if (spawner !== null) rows.push(row('Launches', `A ${UNIT_PRESENTATION[spawner.unit].name} every ${seconds(spawner.interval)}, ${spawner.maxAlive} at most`))
  return {
    kicker: `UNIT · ${DOMAIN_LABELS[def.domain]}`,
    title: item.presentation.name,
    rows,
    note: item.quip,
    footer: sponsored(item.presentation.sponsor),
  }
}

// ----------------------------------------------------------------------------------------------------- strikes

const GUIDANCE_LABELS: Readonly<Record<MissileGuidance, string>> = {
  homing: 'homes on its target, moving or not',
  point: 'flies to where its target stood',
}

const STRIKE_EFFECT_ROWS: { readonly [K in StrikeEffect['kind']]: (effect: EffectOf<StrikeEffect, K>, interceptable: boolean) => InfoRow[] } = {
  missile: (effect, interceptable) => {
    const salvos = effect.salvos > 1 ? ` × ${effect.salvos} salvos` : ''
    const rows = [
      row('Effect', `${effect.damage} damage, radius ${effect.radius}${salvos}`),
      row('Flight', `${effect.speed} map units/s, ${GUIDANCE_LABELS[effect.guidance]}; ${interceptable ? 'SAM Sites can intercept it' : 'cannot be intercepted'}`),
    ]
    if (effect.selfApproval !== 0) rows.push(row('Your approval', `${effect.selfApproval}`, 'bad'))
    return rows
  },
  sanctions: (effect) => [
    row('Effect', `Enemy revenue ×${effect.revenueMultiplier} for ${seconds(effect.durationSeconds)}`),
    row('Enemy approval', `${effect.enemyApproval}`, 'good'),
  ],
  leaflets: (effect) => [
    row('Effect', `Enemy units within ${effect.radius} deal ×${effect.damageMultiplier} damage for ${seconds(effect.durationSeconds)}`),
    row('Enemy approval', `${effect.enemyApproval}`, 'good'),
  ],
}

export function strikeCard(item: StrikeItem): InfoCard {
  const def = STRIKES[item.key]
  const effectRows = STRIKE_EFFECT_ROWS[def.effect.kind] as (effect: StrikeEffect, interceptable: boolean) => InfoRow[]
  return {
    kicker: 'STRIKE',
    title: item.presentation.name,
    rows: [row('Cost', formatMoney(def.cost), 'money'), row('Aim', item.doctrine.rule), ...effectRows(def.effect, def.interceptable)],
    note: item.quip,
    footer: sponsored(item.presentation.sponsor),
  }
}

// --------------------------------------------------------------------------------------------------- buildings

const BUILDING_EFFECTS: { readonly [K in BuildingEffect['kind']]: (effect: EffectOf<BuildingEffect, K>) => string } = {
  revenue: (effect) => `+${formatMoneyRate(effect.perSec)} revenue`,
  deploy_air: () => 'Aircraft deploy from this city',
  deploy_sea: () => 'Ships deploy from this city; coastal cities only',
  sam_site: (effect) => `Shoots aircraft (${effect.damage} dmg, range ${effect.range}) and intercepts a missile every ${seconds(effect.interceptPeriod)}`,
  fortify: (effect) => `City HP ×${effect.hpMultiplier}, city gun damage ×${effect.gunDamageMultiplier}`,
  approval: (effect) => `+${effect.perSec} approval/s`,
}

/** A building option of the open build menu; `objection` is why this city would refuse it, null when it looks fine. */
export function buildingCard(key: BuildingKey, city: MapCityView, objection: RejectReason | null): InfoCard {
  const def = BUILDINGS[key]
  const presentation = BUILDING_PRESENTATION[key]
  const describe = BUILDING_EFFECTS[def.effect.kind] as (effect: BuildingEffect) => string
  return {
    kicker: 'BUILDING',
    title: presentation.name,
    rows: [
      row('Cost', formatMoney(def.cost), 'money'),
      row('Effect', describe(def.effect)),
      objection === null ? row(city.name, `${MET_MARK} Takes a free slot`, 'good') : row(city.name, `${UNMET_MARK} ${REJECT_COPY[objection].long}`, 'bad'),
    ],
    note: null,
    footer: sponsored(presentation.sponsor),
  }
}

// ------------------------------------------------------------------------------------------------------ cities

type Holder = 'you' | 'enemy' | 'neutral'

interface HolderCopy {
  readonly tone: InfoTone
  readonly note: (capital: boolean) => string
}

const CLAIM_NOTE = `Free territory: a land unit claims it by holding it for ${seconds(CAPTURE.uncontestedSeconds)} with no enemy there.`

const HOLDERS: Readonly<Record<Holder, HolderCopy>> = {
  you: { tone: 'player', note: (capital) => (capital ? 'Your capital. Lose it and you lose the war.' : 'Yours. Click it to build.') },
  enemy: {
    tone: 'enemy',
    note: (capital) =>
      capital ? 'Destroy it to win the war. Capitals cannot be captured.' : 'Knock it down to 0 HP, then hold it with a land unit to capture it.',
  },
  neutral: { tone: 'neutral', note: () => CLAIM_NOTE },
}

function holderOf(state: CityLiveState, map: WarMapIndex): Holder {
  if (state.owner === NEUTRAL_OWNER) return 'neutral'
  return state.owner === map.you ? 'you' : 'enemy'
}

function countryName(city: MapCityView): string {
  const country = COUNTRIES.get(city.country)
  if (country === undefined) throw new HudError(`war map city ${city.name} names country ${city.country}, which is not in the content dataset`)
  return country.name
}

function buildingList(state: CityLiveState, slots: number): string {
  const free = Math.max(0, slots - state.buildings.length)
  const names = state.buildings.map((id) => BUILDING_PRESENTATION[buildingKeyOf(id)].name)
  const open = `${free} of ${slots} free`
  return names.length === 0 ? `None (${open})` : `${names.join(' · ')} (${open})`
}

/** A city on the globe as the player knows it (last-known values while it is out of sight). */
export function cityCard(city: MapCityView, state: CityLiveState, slots: number, map: WarMapIndex): InfoCard {
  const holder = holderOf(state, map)
  const copy = HOLDERS[holder]
  const rows: InfoRow[] = [row('Country', countryName(city)), row('Held by', holder === 'you' ? 'You' : map.ownerName(state.owner), copy.tone)]
  if (holder !== 'neutral') {
    rows.push(state.fallen ? row('HP', 'Fallen: 0', 'bad') : row('HP', `${formatWhole(Math.ceil(state.hp))} / ${formatWhole(state.maxHp)}`))
    rows.push(row('Buildings', buildingList(state, slots)))
  }
  if (city.coastal) rows.push(row('Coast', 'Coastal: can build a Port'))
  if (state.captureProgress > 0) {
    const taker = state.captureBy === map.you ? 'You' : map.ownerName(state.captureBy)
    rows.push(row('Being taken', `${taker}: ${Math.floor(state.captureProgress * 100)}%`, state.captureBy === map.you ? 'good' : 'bad'))
  }
  if (!state.inSight) rows.push(row('Intel', 'Last seen: out of sight now'))
  return { kicker: city.capital ? 'CAPITAL' : 'CITY', title: city.name, rows, note: copy.note(city.capital), footer: null }
}
